import { useState } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Action, Field, PageHeader, Panel, PanelHeader } from "./ui";
import { STAFF_TYPE_LABELS } from "./StaffManager";

export default function PortalSettings() {
  const { api, user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", new_password_confirmation: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("A nova senha deve ter ao menos 8 caracteres.");
      return;
    }
    if (form.new_password !== form.new_password_confirmation) {
      toast.error("A confirmação da nova senha não confere.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", form);
      toast.success("Senha alterada. A senha anterior não funciona mais.");
      setForm({ current_password: "", new_password: "", new_password_confirmation: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div data-testid="portal-settings-page" className="space-y-7">
      <PageHeader kicker="Sua conta" title="Configurações" subtitle="Dados da sua conta e segurança de acesso." />

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <Panel testId="settings-security">
          <PanelHeader
            label="Segurança"
            title="Alterar senha"
            hint="Após a troca, a senha anterior deixa de funcionar imediatamente e as outras sessões são encerradas."
          />
          <form onSubmit={submit} className="mt-6 max-w-md space-y-5">
            <Field label="Senha atual" htmlFor="current-password">
              <Input
                id="current-password"
                data-testid="current-password-input"
                type="password"
                autoComplete="current-password"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
                required
              />
            </Field>
            <Field label="Nova senha" htmlFor="new-password" hint="Mínimo de 8 caracteres.">
              <Input
                id="new-password"
                data-testid="new-password-input"
                type="password"
                autoComplete="new-password"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
                required
              />
            </Field>
            <Field label="Confirmar nova senha" htmlFor="new-password-confirmation">
              <Input
                id="new-password-confirmation"
                data-testid="new-password-confirmation-input"
                type="password"
                autoComplete="new-password"
                value={form.new_password_confirmation}
                onChange={(e) => setForm({ ...form, new_password_confirmation: e.target.value })}
                className="h-11 rounded-xl text-[14.5px]"
                required
              />
            </Field>
            <Action type="submit" icon={KeyRound} data-testid="change-password-submit" disabled={saving}>
              {saving ? "Alterando…" : "Alterar senha"}
            </Action>
          </form>
        </Panel>

        <Panel testId="settings-account" className="h-fit">
          <PanelHeader label="Conta" title="Seus dados" />
          <dl className="mt-5 space-y-3.5 text-[14.5px]">
            <div>
              <dt className="text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-500">Nome</dt>
              <dd className="mt-1 text-ink-800">{user?.name}</dd>
            </div>
            <div>
              <dt className="text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-500">Nome de usuário</dt>
              <dd data-testid="settings-username" className="mt-1 font-mono text-ink-800">
                {user?.username || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-500">E-mail</dt>
              <dd className="mt-1 text-ink-800">{user?.email || "não cadastrado"}</dd>
            </div>
            {user?.staff_type ? (
              <div>
                <dt className="text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-500">Tipo</dt>
                <dd className="mt-1 text-ink-800">{STAFF_TYPE_LABELS[user.staff_type] || user.staff_type}</dd>
              </div>
            ) : null}
          </dl>
          <p className="mt-5 flex items-start gap-2 text-[13.5px] leading-relaxed text-ink-500">
            <ShieldCheck size={16} className="mt-0.5 shrink-0 text-sage-700" aria-hidden="true" />
            Ninguém, nem a administração, consegue visualizar a senha de outra pessoa: guardamos apenas o hash.
          </p>
        </Panel>
      </div>
    </div>
  );
}
